// Explicit type reference as this is not transpiled when published
/// <reference types="../../types/normalize-html-whitespace" />

import type { TFunctionKeys, TFunctionResult } from 'i18next';
import normalize from 'normalize-html-whitespace';

import { Duration } from './Duration';
import { Money } from './Money';
import { Period } from './Period';

export type { TFunctionResult };

export type FormattableValue = number | Date | Money | Period | Duration;
export type InterpolatableValue = string | FormattableValue;
export type TValues = { [key: string]: InterpolatableValue };

export interface TFunction<Result extends TFunctionResult = string> {
  (i18nKey: string): Result;
  (i18nKey: string, values: TValues): Result;
  (strings: TemplateStringsArray): Result;
}

type Translator<Result> = (i18nKey: string, values?: TValues) => Result;

export const translate = <Result extends TFunctionResult>(translator: Translator<Result>): TFunction<Result> =>
  function (strings: TFunctionKeys, values?: TValues): Result {
    // If we have a string (i.e. t('key'))
    if (typeof strings === 'string') {
      return translator(normalize(strings), values);
    }

    // If we have a tagged template literal (i.e. t`key`)
    // If we have more than one piece, we have interpolation (t`key.${something}`)
    // We don't support that because we can't find all the i18nKeys statically
    if (strings.length > 1) {
      throw new Error('T cannot be used to interpolate - pass values as a second argument');
    }
    // Values aren't supported with a tagged template literal
    return translator(normalize(strings[0]));
  };
